import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import moment from "moment";
import Brand from "./Brand";
import UserHeader from "./UserHeader";

// En-tête de l'espace connecté : logo, navigation principale, menu
// administration, notifications et compte. Une seule structure, adaptée
// en CSS (navigation horizontale sur grand écran, barre d'onglets sur mobile).
export default function AppHeader({
    navItems,
    isAdmin,
    adminItems,
    adminActive,
    adminBadgeFor,
    adminEventCount,
    adminMenuOpen,
    onToggleAdminMenu,
    onCloseAdminMenu,
    userEventCount,
    eventMenuOpen,
    onToggleEventMenu,
    onCloseEventMenu,
    eventItems,
    onClearEvents
}) {
    // Fermeture des menus flottants au clic extérieur et à la touche Échap.
    useEffect(() => {
        if (!adminMenuOpen && !eventMenuOpen) {
            return undefined;
        }

        const handlePointer = (event) => {
            const target = event.target instanceof Element ? event.target : null;
            if (adminMenuOpen && !(target && target.closest('[data-lk-menu="admin"]'))) {
                onCloseAdminMenu();
            }
            if (eventMenuOpen && !(target && target.closest('[data-lk-menu="events"]'))) {
                onCloseEventMenu();
            }
        };
        const handleKey = (event) => {
            if (event.key === "Escape") {
                onCloseAdminMenu();
                onCloseEventMenu();
            }
        };

        document.addEventListener("pointerdown", handlePointer);
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("pointerdown", handlePointer);
            document.removeEventListener("keydown", handleKey);
        };
    }, [adminMenuOpen, eventMenuOpen, onCloseAdminMenu, onCloseEventMenu]);

    const renderAdminLinks = () => adminItems.map((item) => {
        const badge = adminBadgeFor(item.to);
        return (
            <Link
                key={item.to}
                to={item.to}
                className="lk-popover__link"
                onClick={onCloseAdminMenu}
                aria-label={badge > 0 ? `${item.label} (${badge})` : undefined}
            >
                <i className={item.icon} aria-hidden="true"></i>
                <span>{item.label}</span>
                {badge > 0 && <span className="lk-badge lk-badge--inline">{badge}</span>}
            </Link>
        );
    });

    const renderAdminMenu = (variant) => {
        if (!isAdmin) {
            return null;
        }
        const isTab = variant === "tab";
        const triggerClass = (isTab ? "lk-tab" : "lk-nav__link") + (adminActive ? " is-active" : "");
        return (
            <div className={`${isTab ? "" : "lk-nav__item "}lk-menu${adminMenuOpen ? " is-open" : ""}`} data-lk-menu="admin">
                <button
                    type="button"
                    className={triggerClass}
                    onClick={onToggleAdminMenu}
                    aria-expanded={adminMenuOpen}
                    aria-haspopup="true"
                >
                    {isTab ? (
                        <>
                            <i className="pi pi-shield" aria-hidden="true"></i>
                            <span>Admin</span>
                            {adminEventCount > 0 && <span className="lk-badge">{adminEventCount}</span>}
                        </>
                    ) : (
                        <>
                            <span>Administration</span>
                            {adminEventCount > 0 && <span className="lk-badge lk-badge--inline">{adminEventCount}</span>}
                            <i className="pi pi-angle-down" aria-hidden="true"></i>
                        </>
                    )}
                </button>
                <div className={`lk-popover${isTab ? "" : " lk-popover--left"}`} role="menu">
                    {renderAdminLinks()}
                </div>
            </div>
        );
    };

    return (
        <header id="header" className="lk-header lk-header--app">
            <div className="lk-header__top">
                <Brand />

                <nav className="lk-nav" aria-label="Navigation principale">
                    {navItems.map((item) => (
                        <div className="lk-nav__item" key={item.to}>
                            <Link to={item.to} className={`lk-nav__link${item.active ? " is-active" : ""}`} aria-current={item.active ? "page" : undefined}>
                                {item.label}
                            </Link>
                        </div>
                    ))}
                    {renderAdminMenu("nav")}
                </nav>

                <div className="lk-header__actions">
                    <Link to="/contact" className="lk-header__textlink" title="Nous contacter">
                        <i className="pi pi-envelope" aria-hidden="true"></i>
                        <span>Contact</span>
                    </Link>

                    <div className={`lk-menu${eventMenuOpen ? " is-open" : ""}`} data-lk-menu="events">
                        <button
                            type="button"
                            className="lk-icon-btn"
                            onClick={onToggleEventMenu}
                            aria-expanded={eventMenuOpen}
                            aria-haspopup="true"
                            aria-label={userEventCount > 0 ? `Notifications (${userEventCount} non lues)` : "Notifications"}
                        >
                            <i className="pi pi-bell" aria-hidden="true"></i>
                            {userEventCount > 0 && <span className="lk-badge">{userEventCount}</span>}
                        </button>
                        <div className="lk-popover" role="dialog" aria-label="Notifications">
                            <div className="lk-popover__header">
                                <span className="lk-popover__title">Notifications</span>
                                <div>
                                    {isAdmin && eventItems.length > 0 && (
                                        <button type="button" className="lk-textbtn lk-textbtn--danger" onClick={onClearEvents}>
                                            Effacer
                                        </button>
                                    )}
                                    <button type="button" className="lk-textbtn" onClick={onCloseEventMenu}>
                                        Fermer
                                    </button>
                                </div>
                            </div>
                            {eventItems.length === 0 ? (
                                <div className="lk-popover__empty">Aucune notification non lue.</div>
                            ) : (
                                <ul className="lk-notif-list">
                                    {eventItems.map((item) => (
                                        <li className="lk-notif" key={`${item.code}-${item.referenceId ?? item.createdAtUtc}`}>
                                            <span className="lk-notif__title">{item.title}</span>
                                            <span className="lk-notif__message">{item.message}</span>
                                            <span className="lk-notif__date">{moment(item.createdAtUtc).format("DD/MM/YYYY HH:mm")}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    <UserHeader />
                </div>
            </div>

            <nav className="lk-tabs" aria-label="Sections">
                {navItems.map((item) => (
                    <Link key={item.to} to={item.to} className={`lk-tab${item.active ? " is-active" : ""}`} aria-current={item.active ? "page" : undefined}>
                        <i className={item.icon} aria-hidden="true"></i>
                        <span>{item.label}</span>
                    </Link>
                ))}
                {renderAdminMenu("tab")}
            </nav>
        </header>
    );
}
